/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ISliderChangeNotification, Slider } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { IRefineItemData } from './common';
import { getSliderLabels, getSliderThumbs } from './utilities';

/**
 * IRangeRefineSlider Component Interface.
 */
export interface IRangeRefineSliderComponent extends IComponent<IRefineRangeSliderProps> {}

/**
 * IRangeRefineSlider Data interface.
 */
export interface IRefinerItemData {
    refinerItemData: IRefineItemData;
}

/**
 * Refine range slider props Interface.
 */
export interface IRefineRangeSliderProps extends IComponentProps<IRefinerItemData> {
    sliderClassName?: string;
    sliderRangeMinVal: number;
    sliderRangeMaxVal: number;
    selectedSliderMinVal?: number;
    selectedSliderMaxVal?: number;
    isDisabled?: boolean;
    sliderId?: string;
    sliderKey?: string;
    step?: number;
    shouldShowLabels?: boolean;
    shouldShowTooltip?: boolean;

    // This property is added just to make the component rerender in order to fix the slider issue.
    isRefinerExpanded?: boolean;
    orientation?: 'horizontal' | 'vertical';
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    formatLabelString?(selectedRangeValue: string, currencyCode?: string): string;
    formatTooltipValue?(sliderValue: string, currencyCode?: string): string;
    onRangeChange?(firstValue: number, secondValue: number): void;
}

/**
 * Range refine slider component.
 * @param props - IRefineRangeSliderProps.
 * @returns - Renders the Range Refine slider.
 */
const RangeRefineSliderFunctionalComponent: React.FC<IRefineRangeSliderProps> = (props: IRefineRangeSliderProps) => {
    const {
        isDisabled,
        sliderRangeMinVal,
        formatLabelString,
        sliderRangeMaxVal,
        onRangeChange,
        minValueSliderThumbAriaLabel,
        shouldShowLabels,
        maxValueSliderThumbAriaLabel,
        selectedSliderMinVal,
        selectedSliderMaxVal,
        sliderClassName,
        step,
        orientation,
        shouldShowTooltip,
        sliderKey,
        sliderId,
        data: { refinerItemData }
    } = props;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- For string the check is required.
    const sliderLocalId = sliderId || 'slider_01';
    const formattedMinValue = formatLabelString
        ? formatLabelString(sliderRangeMinVal.toString(), refinerItemData.unitText)
        : sliderRangeMinVal.toString();
    const formattedMaxValue = formatLabelString
        ? formatLabelString(sliderRangeMaxVal.toString(), refinerItemData.unitText)
        : sliderRangeMaxVal.toString();
    const sliderLabels = getSliderLabels(sliderLocalId, formattedMinValue, formattedMaxValue);

    const selectedMinValue = (selectedSliderMinVal ?? sliderRangeMinVal).toString();
    const selectedMaxValue = (selectedSliderMaxVal ?? sliderRangeMaxVal).toString();

    const sliderThumbs = getSliderThumbs(
        sliderLocalId,
        selectedMinValue,
        formattedMinValue,
        selectedMaxValue,
        formattedMaxValue,
        sliderRangeMaxVal.toString(),
        minValueSliderThumbAriaLabel,
        maxValueSliderThumbAriaLabel
    );

    const ariaAttributes = {
        'aria-disabled': isDisabled
    };

    /**
     * HandleRangeTooltipText.
     * @param tooltip - Tooltip value.
     * @returns - Formatted tooltip value.
     */
    const handleRangeTooltipText = (tooltip: number): string => {
        return props.formatTooltipValue ? props.formatTooltipValue(tooltip.toString(), refinerItemData.unitText) : `${tooltip}`;
    };

    /**
     * HandleRangeTooltipText.
     * @param sliderChangeNotification - Slider change notification value.
     */
    const onRangeUpdate = (sliderChangeNotification: ISliderChangeNotification) => {
        // Need to filter out mousemove events as these cause errors after the menu updates and slider re-renders
        if (sliderChangeNotification.eventType !== 'mousemove') {
            getSliderThumbs(
                sliderLocalId,
                sliderChangeNotification.firstThumbValue.toString(),
                formattedMinValue,
                sliderChangeNotification.secondThumbValue!.toString(),
                formattedMaxValue,
                sliderRangeMaxVal.toString(),
                minValueSliderThumbAriaLabel,
                maxValueSliderThumbAriaLabel
            );
            onRangeChange?.(sliderChangeNotification.firstThumbValue, sliderChangeNotification.secondThumbValue!);
        }
    };

    const onRangeUpdateEnd = (sliderChangeNotification: ISliderChangeNotification) => {
        getSliderThumbs(
            sliderLocalId,
            sliderChangeNotification.firstThumbValue.toString(),
            formattedMinValue,
            sliderChangeNotification.secondThumbValue!.toString(),
            formattedMaxValue,
            sliderRangeMaxVal.toString(),
            minValueSliderThumbAriaLabel,
            maxValueSliderThumbAriaLabel
        );
        onRangeChange?.(sliderChangeNotification.firstThumbValue, sliderChangeNotification.secondThumbValue!);
    };

    return (
        <Slider
            id={sliderLocalId}
            key={sliderKey}
            sliderKey={sliderKey}
            className={`${sliderClassName ?? ''} ms-refine-submenu__range-refiner`}
            inForm={false}
            min={sliderRangeMinVal}
            max={sliderRangeMaxVal}
            step={step}
            orientation={orientation}
            labels={sliderLabels}
            showLabels={shouldShowLabels}
            showTooltip={shouldShowTooltip}
            sliderThumbs={sliderThumbs}
            onChangeEnd={onRangeUpdateEnd}
            onChange={onRangeUpdate}
            handleTooltipText={handleRangeTooltipText}
            {...ariaAttributes}
        />
    );
};

RangeRefineSliderFunctionalComponent.defaultProps = {
    shouldShowLabels: true,
    shouldShowTooltip: true,
    orientation: 'horizontal',
    step: 1
};

/**
 * Range Refine Input Component.
 */
export const RangeRefineSliderComponent: React.FunctionComponent<IRefineRangeSliderProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRangeRefineSliderComponent
>('Range-Refine-Slider-Item', { component: React.memo(RangeRefineSliderFunctionalComponent) });
